import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Activity, ExternalLink, CheckCircle, Clock } from 'lucide-react';

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, [statusFilter]);

  const fetchTransactions = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/transactions${params}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Transactions">
      <div className="space-y-6" data-testid="admin-transactions-page">
        <div className="flex items-center justify-between pb-6">
          <p className="text-muted-foreground">Monitor all payment transactions</p>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : transactions.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Transactions</h3>
              <p className="text-muted-foreground">Payment transactions will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="text-lg">Transaction Monitor ({transactions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div 
                    key={tx.id} 
                    className="flex items-center justify-between p-4 bg-card border border-gray-200 dark:border-white/10 rounded-lg shadow-sm"
                    data-testid={`tx-row-${tx.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {tx.status === 'confirmed' ? (
                        <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-emerald-500" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                          <Clock className="h-5 w-5 text-yellow-500" />
                        </div>
                      )}
                      <div>
                        <p className="font-bold font-mono">{tx.amount} {tx.currency}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          TX: {tx.tx_hash?.slice(0, 16)}...
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Block: {tx.block_number} | {tx.confirmations} confirmations
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={tx.status === 'confirmed' 
                        ? 'bg-emerald-500/20 text-emerald-500' 
                        : 'bg-yellow-500/20 text-yellow-500'
                      }>
                        {tx.status}
                      </Badge>
                      <a 
                        href={`https://bscscan.com/tx/${tx.tx_hash}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button size="icon" variant="ghost">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTransactions;
